import { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ReactNode } from 'react';
import { cx, focusRing } from './cx';
import { ActionButton, ActionMenuButton, type Action } from './Actions';
import { Disclosure } from './Disclosure';

export interface PageHeaderProps {
  title: ReactNode;
  /**
   * One line of context: counts, period, owner. Numbers in it should be
   * wrapped in .record. Two lines at most on a phone; an explanation longer
   * than a sentence belongs in `help`.
   */
  meta?: ReactNode;
  /** Above the title. A section name or a back link. */
  eyebrow?: ReactNode;
  /**
   * The one action the page exists for ("Add a grant"). Primary, at the
   * right on a desk and full width under the title on a phone, where a
   * thumb reaches it.
   */
  primaryAction?: Action;
  /**
   * Everything else the page offers. Ghost buttons beside the primary on a
   * desk; below 768px one "More actions" control beside the title opens
   * them as a sheet of 48px rows, instead of a stack of wrapped words.
   */
  secondaryActions?: Action[];
  /**
   * How the page works, for the person who wants to know. Rendered as a
   * "How this page works" Disclosure at the foot of the page, where it is
   * one tap away and not between the title and the register.
   */
  help?: ReactNode;
  /** The Disclosure's summary. Defaults to "How this page works". */
  helpTitle?: ReactNode;
  /**
   * Free-form actions, as before. Still supported for pages not yet moved
   * to `primaryAction` and `secondaryActions`; they wrap beside the title.
   */
  actions?: ReactNode;
  className?: string;
  children?: ReactNode;
}

/** The page-foot slot the app shell renders after the route. */
export const PAGE_FOOT_ID = 'ct-page-foot';

/**
 * The single h1 recipe: Instrument Sans 600, 26px on a phone, 34px from
 * 640px and 40px from 768px, tracking -0.018em, ink. Meta under it in
 * ink-3, actions right.
 *
 * On a 360px phone it used to be the whole first screen: a 34px title of
 * three or four lines (titles here are organisation, grant and call names,
 * 40 to 60 characters), an essay of meta, and four or five wrapped ghost
 * buttons, all before the register the person came to read. Below 640px
 * the title steps down to the h2 size, the meta stops at two lines, the
 * page's one primary action is a full-width button and the rest are in a
 * sheet behind one control.
 */
export function PageHeader({
  title,
  meta,
  eyebrow,
  primaryAction,
  secondaryActions,
  help,
  helpTitle = 'How this page works',
  actions,
  className,
  children,
}: PageHeaderProps) {
  const secondary = secondaryActions ?? [];
  const hasDeskActions = Boolean(actions || primaryAction || secondary.length);
  return (
    <>
      <header className={cx('mb-4 flex flex-wrap items-end justify-between gap-x-4 gap-y-3 sm:mb-6', className)}>
        <div className="min-w-0 max-w-prose flex-1 basis-full md:basis-auto">
          {/* Not uppercase mono. The direction retired that eyebrow by name,
              and the only call site in the product renders a back link
              ("← All calls"): a navigation control set in the record face at
              11px, which is neither a record nor legible. */}
          {eyebrow ? <p className="mb-2.5 text-sm font-semibold text-forest">{eyebrow}</p> : null}
          {/* The More actions control floats, so a long title (an
              organisation's full legal name) gives up its width on the
              first line only, not on every line. */}
          <div className="flow-root">
            {secondary.length > 0 ? (
              <ActionMenuButton actions={secondary} label="More actions" className="float-right -mr-2.5 -mt-1.5 ml-2 md:hidden" />
            ) : null}
            <h1 className="font-display text-2xl font-semibold leading-[1.12] tracking-[-0.018em] text-ink [font-optical-sizing:auto] [overflow-wrap:break-word] [text-wrap:wrap] sm:text-3xl sm:leading-[1.08] md:text-4xl md:[text-wrap:balance]">
              {title}
            </h1>
          </div>
          {meta ? <PageMeta>{meta}</PageMeta> : null}
          {children}
        </div>
        {/* min-w-0, not shrink-0. Unable to shrink, the actions kept the width
            of all their buttons on one line, so on a 360px phone the Grants
            and Pipeline headers held the page at 793px and Funders at 496px.
            Where they fit nothing changes; where they do not, they wrap. */}
        {hasDeskActions ? (
          <div className={cx('flex min-w-0 flex-wrap items-center gap-2', primaryAction && 'w-full md:w-auto')}>
            {actions}
            {secondary.map((a, i) => (
              <ActionButton key={a.key ?? (typeof a.label === 'string' ? a.label : i)} action={a} className="hidden md:inline-flex" />
            ))}
            {primaryAction ? (
              <ActionButton action={primaryAction} variant="primary" size="md" className="w-full md:w-auto" />
            ) : null}
          </div>
        ) : null}
      </header>
      {help ? <PageHelp title={helpTitle}>{help}</PageHelp> : null}
    </>
  );
}

/**
 * The meta line, two lines at most on a phone, with the rest one tap away.
 *
 * Clamping alone cut the sentence off with an ellipsis and no way to read
 * the end of it, and most pages still pass their explanation here rather
 * than in `help`: on a 360px phone Payments read "approved by a second
 * person before anything..." and the plan panel lost "comes straight back
 * when you subscribe". When the clamp actually cuts something, a "Show
 * all" text button opens it. A screen reader always had the whole text;
 * the clamp is visual only.
 */
function PageMeta({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLParagraphElement>(null);
  const [open, setOpen] = useState(false);
  const [cut, setCut] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || open) return;
    const measure = () => setCut(el.scrollHeight - el.clientHeight > 1);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [open, children]);
  return (
    <>
      <p ref={ref} className={cx('mt-1.5 text-sm text-ink-3 sm:mt-2', !open && 'line-clamp-2 sm:line-clamp-none')}>
        {children}
      </p>
      {cut || open ? (
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
          className={cx(
            '-mb-2.5 -mt-1 inline-flex min-h-tap items-center rounded-ct-sm text-sm font-semibold text-forest active:bg-forest-faint sm:hidden',
            focusRing,
          )}
        >
          {open ? 'Show less' : 'Show all'}
        </button>
      ) : null}
    </>
  );
}

/**
 * "How this page works", at the foot of the page. The app shell renders an
 * empty slot after every route and this portals into it; a page outside
 * the shell (the demo) gets it in place, under the header.
 */
function PageHelp({ title, children }: { title: ReactNode; children: ReactNode }) {
  // undefined: not looked yet. null: looked, and there is no slot.
  const [slot, setSlot] = useState<HTMLElement | null | undefined>(undefined);
  useLayoutEffect(() => {
    setSlot(document.getElementById(PAGE_FOOT_ID));
  }, []);
  if (slot === undefined) return null;
  const body = (
    <Disclosure summary={title} className="mt-8 border-t">
      <div className="max-w-prose space-y-2">{children}</div>
    </Disclosure>
  );
  return slot ? createPortal(body, slot) : body;
}
